import { usePhonePlans } from '@/hooks/usePhonePlans';
import { useOperators } from '@/hooks/useOperator';
import { useCurrentOrganization } from '@/hooks/useOrganization';
import { cn } from '@/lib/utils';
import { Phone, Wifi } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import type { CommandLineForm } from './CommandLineView';
import { formatDataAllowance, formatPrice } from '@/lib/format';

const operatorImages: Record<string, string> = {
  Orange: '/images/operators/orange.webp',
  SFR: '/images/operators/sfr.svg',
  Bouygues: '/images/operators/bouygues.png',
};


interface ForfaitStepProps {
  form: UseFormReturn<CommandLineForm>;
}

export function ForfaitStep({ form }: ForfaitStepProps) {
  const { data: operators, isLoading: operatorsLoading } = useOperators();
  const { data: organization } = useCurrentOrganization();
  const [availableTypes, setAvailableTypes] = useState<string[]>([]);
  const [searchParams] = useSearchParams();
  const isLocked = !!searchParams.get('planId');

  const operatorId = form.watch('operatorId');
  const mobileLineType = form.watch('mobileLineType');
  const planId = form.watch('planId');

  const selectedOperator = useMemo(
    () => operators?.find((o) => o.id === operatorId),
    [operators, operatorId]
  );

  const { data: plans, isLoading: plansLoading } = usePhonePlans(
    organization?.id,
    operatorId,
    mobileLineType
  );

  const selectedPlan = useMemo(
    () => plans?.find((p) => p.id === planId),
    [plans, planId]
  );

  // Update available types when operator changes
  useEffect(() => {
    if (selectedOperator?.availableTypes) {
      const filtered = selectedOperator.availableTypes.filter(
        (type: string) => type === 'dataonly' || type === 'classic'
      );
      setAvailableTypes(filtered);
      form.setValue('operatorLabel', selectedOperator.name);
      if (filtered.length === 1) {
        form.setValue('mobileLineType', filtered[0]);
      }
    } else if (!operatorId) {
      setAvailableTypes([]);
      form.setValue('mobileLineType', 'classic');
      form.setValue('operatorLabel', '');
    }
  }, [selectedOperator, operatorId, form]);

  if (operatorsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-6 w-6 border-2 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (isLocked) {
    return (
      <div className="space-y-8">
        <h3 className="text-lg font-bold text-slate-900">
          Forfait sélectionné
        </h3>

        {plansLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin h-6 w-6 border-2 border-emerald-500 border-t-transparent rounded-full" />
          </div>
        ) : selectedPlan ? (
          <div className="rounded-2xl border border-emerald-200 bg-white shadow-sm overflow-hidden">
            <div className="flex items-center gap-4 px-6 py-5 border-b border-slate-100">
              <img
                src={
                  operatorImages[selectedPlan.operator.name] ||
                  '/images/operators/default.svg'
                }
                alt={selectedPlan.operator.name}
                className="h-12 w-12 object-contain"
              />
              <div>
                <p className="text-lg font-bold text-slate-900">
                  {selectedPlan.name}
                </p>
                <p className="text-sm text-slate-500 flex items-center gap-1.5">
                  {selectedPlan.operator.name}
                  <span className="text-slate-300">·</span>
                  {selectedPlan.isDataOnly ? (
                    <>
                      <Wifi className="h-3.5 w-3.5" />
                      SIM Data
                    </>
                  ) : (
                    <>
                      <Phone className="h-3.5 w-3.5" />
                      Ligne mobile
                    </>
                  )}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 p-6">
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4">
                <p className="text-xs font-medium text-emerald-700">
                  Prix mensuel
                </p>
                <p className="text-2xl font-bold text-slate-900 mt-1">
                  {formatPrice(selectedPlan.price)}
                  <span className="text-sm font-normal text-slate-500">
                    {' '}
                    /mois
                  </span>
                </p>
              </div>
              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-xs font-medium text-slate-500">
                  Enveloppe data
                </p>
                <p className="text-2xl font-bold text-slate-900 mt-1">
                  {formatDataAllowance(selectedPlan.dataLimitMo)}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 text-center">
            <p className="text-sm text-slate-500">Forfait introuvable.</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <h3 className="text-lg font-bold text-slate-900">
        Choix du forfait
      </h3>

      {/* Operator selection */}
      <section>
        <label className="block text-sm font-medium text-slate-700 mb-3">Opérateur</label>
        <div className="flex gap-3">
          {operators?.map((op) => (
            <button
              key={op.id}
              type="button"
              onClick={() => {
                form.setValue('operatorId', op.id);
                form.setValue('planId', '');
              }}
              className={cn(
                'flex flex-col items-center gap-2 rounded-xl border p-4 w-36 cursor-pointer transition-all',
                operatorId === op.id
                  ? 'border-emerald-500 bg-emerald-50'
                  : 'border-slate-200 hover:border-emerald-200 hover:shadow-md hover:-translate-y-0.5'
              )}
            >
              <img
                src={operatorImages[op.name] || '/images/operators/default.svg'}
                alt={op.name}
                className="w-12 h-12 object-contain"
              />
              <span className="text-sm font-medium text-slate-900">
                {op.name}
              </span>
            </button>
          ))}
        </div>
      </section>

      {/* Line type selection — shown after operator */}
      {operatorId && (
        <section>
          <label className="block text-sm font-medium text-slate-700 mb-3">
            Type de ligne
          </label>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => {
                form.setValue('mobileLineType', 'classic');
                form.setValue('planId', '');
              }}
              className={cn(
                'flex items-center gap-3 rounded-xl border px-4 py-3 flex-1 cursor-pointer transition-all',
                mobileLineType === 'classic'
                  ? 'border-emerald-500 bg-emerald-50'
                  : 'border-slate-200 hover:border-emerald-200 hover:shadow-md hover:-translate-y-0.5'
              )}
            >
              <Phone className="h-4 w-4 text-slate-500 shrink-0" />
              <div>
                <p className="text-sm font-medium text-slate-900">
                  Ligne mobile
                </p>
                <p className="text-xs text-slate-500">Appels, SMS, Data</p>
              </div>
            </button>
            {availableTypes.includes('dataonly') && (
              <button
                type="button"
                onClick={() => {
                  form.setValue('mobileLineType', 'dataonly');
                  form.setValue('planId', '');
                }}
                className={cn(
                  'flex items-center gap-3 rounded-xl border px-4 py-3 flex-1 cursor-pointer transition-all',
                  mobileLineType === 'dataonly'
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-slate-200 hover:border-emerald-200 hover:shadow-md hover:-translate-y-0.5'
                )}
              >
                <Wifi className="h-4 w-4 text-slate-500 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-slate-900">SIM Data</p>
                  <p className="text-xs text-slate-500">Internet uniquement</p>
                </div>
              </button>
            )}
          </div>
        </section>
      )}

      {/* Plan selection — shown after line type */}
      {operatorId && mobileLineType && (
        <section>
          <label className="block text-sm font-medium text-slate-700 mb-3">
            Forfait{' '}
            <span className="text-emerald-600 font-semibold">
              {form.watch('operatorLabel')}
            </span>
          </label>

          {plansLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin h-6 w-6 border-2 border-emerald-500 border-t-transparent rounded-full" />
            </div>
          ) : plans && plans.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {plans.map((plan) => (
                <button
                  key={plan.id}
                  type="button"
                  onClick={() => {
                    form.setValue('planId', plan.id);
                    form.setValue(
                      'mobileLineType',
                      plan.isDataOnly ? 'dataonly' : 'classic'
                    );
                  }}
                  className={cn(
                    'rounded-xl border p-4 text-left cursor-pointer transition-all',
                    planId === plan.id
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-slate-200 hover:border-emerald-200 hover:shadow-md hover:-translate-y-0.5'
                  )}
                >
                  <p className="text-sm font-medium text-slate-900">
                    {plan.name}
                  </p>
                  <p className="text-lg font-bold text-emerald-600 mt-1">
                    {(plan.price / 100).toFixed(2)} €
                    <span className="text-xs font-normal text-slate-500">
                      {' '}
                      / mois
                    </span>
                  </p>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-sm text-slate-500">
                Aucun forfait disponible pour cet opérateur.
              </p>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
